import React, { useEffect, useState } from 'react';
import { api, API_BASE } from '../../api/client';

import { ta } from '../../i18n/adminT';
const emptyForm = { host: '', port: '587', secure: false, user: '', password: '', fromName: 'ExelFlow', fromEmail: '' };

export default function SmtpSettings() {
  const [form, setForm] = useState(emptyForm);
  const [hasPassword, setHasPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testStatus, setTestStatus] = useState(null); // null | 'sending' | 'ok' | 'error'
  const [testError, setTestError] = useState('');

  const [msStatus, setMsStatus] = useState(null); // { configuredOnServer, connected, accountEmail, active }
  const [msDisconnecting, setMsDisconnecting] = useState(false);
  const [msRedirectMessage, setMsRedirectMessage] = useState(null); // { type: 'success'|'error', text }

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getSmtpSettings();
      if (res.data) {
        setForm({
          host: res.data.host || '',
          port: String(res.data.port || '587'),
          secure: Boolean(res.data.secure),
          user: res.data.user || '',
          password: '',
          fromName: res.data.fromName || 'ExelFlow',
          fromEmail: res.data.fromEmail || '',
        });
        setHasPassword(Boolean(res.data.hasPassword));
      }
    } finally {
      setLoading(false);
    }
  };

  const loadMsStatus = async () => {
    try {
      const res = await api.getSmtpOAuthMicrosoftStatus();
      setMsStatus(res.data);
    } catch {
      // in caso di errore la card Microsoft resta semplicemente vuota
    }
  };

  useEffect(() => {
    load();
    loadMsStatus();

    // Se si arriva qui appena tornati dal login Microsoft, mostra l'esito e
    // ripulisci l'indirizzo (per non ripetere il messaggio a un refresh).
    const params = new URLSearchParams(window.location.search);
    const msOAuth = params.get('msOAuth');
    if (msOAuth === 'success') {
      setMsRedirectMessage({ type: 'success', text: ta('Account Microsoft collegato correttamente.') });
    } else if (msOAuth === 'error') {
      setMsRedirectMessage({ type: 'error', text: ta(params.get('message') || 'Collegamento con Microsoft non riuscito.') });
    }
    if (msOAuth) {
      const url = new URL(window.location.href);
      url.searchParams.delete('msOAuth');
      url.searchParams.delete('message');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  const handleConnectMicrosoft = () => {
    window.location.href = `${API_BASE}/admin/smtp-oauth-microsoft/connect`;
  };

  const handleDisconnectMicrosoft = async () => {
    if (!window.confirm(ta('Scollegare l\'account Microsoft? Le email torneranno a usare la configurazione SMTP classica qui sotto, se presente.'))) return;
    setMsDisconnecting(true);
    try {
      await api.disconnectSmtpOAuthMicrosoft();
      await loadMsStatus();
    } finally {
      setMsDisconnecting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!form.host.trim() || !form.user.trim() || !form.fromEmail.trim()) {
      setError(ta('Host, utente ed email mittente sono obbligatori.'));
      return;
    }
    if (!hasPassword && !form.password) {
      setError(ta('La password SMTP è obbligatoria alla prima configurazione.'));
      return;
    }

    setSaving(true);
    try {
      await api.saveSmtpSettings({ ...form, port: parseInt(form.port, 10) });
      setSuccess(true);
      setHasPassword(true);
      setForm((f) => ({ ...f, password: '' }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (e) => {
    e.preventDefault();
    if (!testEmail.trim()) return;
    setTestStatus('sending');
    setTestError('');
    try {
      await api.testSmtpSettings(testEmail.trim());
      setTestStatus('ok');
    } catch (err) {
      setTestStatus('error');
      setTestError(err.message);
    }
  };

  if (loading) return null;

  return (
    <div>
      <h1 className="admin-page-title">{ta('Impostazioni SMTP')}</h1>
      <p className="admin-page-subtitle">
        {ta('Configura l\'invio email usato per le notifiche: recupero password e creazione di nuovi account amministratore.')}
      </p>

      {msStatus?.configuredOnServer && (
        <div className="admin-card" style={{ maxWidth: 480 }}>
          <h3 style={{ marginBottom: 10 }}>{ta('Microsoft 365')}</h3>
          <p style={{ fontSize: '0.85rem', color: 'var(--muted-text)', marginBottom: 14 }}>
            {ta('Alternativa moderna alla password SMTP, per i tenant Microsoft 365 che disattivano le "password per le app": si collega un account con un vero login Microsoft, non con utente e password inseriti a mano.')}
          </p>

          {msRedirectMessage && (
            <div
              style={{
                background: msRedirectMessage.type === 'success' ? 'rgba(62,142,126,0.12)' : undefined,
                color: msRedirectMessage.type === 'success' ? 'var(--teal-green-dark)' : undefined,
                borderRadius: 'var(--radius-control)', padding: '10px 14px', fontSize: '0.85rem', marginBottom: 16,
              }}
              className={msRedirectMessage.type === 'error' ? 'form-error' : undefined}
            >
              {msRedirectMessage.text}
            </div>
          )}

          {msStatus.connected ? (
            <>
              <p style={{ fontSize: '0.9rem', marginBottom: 14 }}>
                {ta('Connesso come')} <strong>{msStatus.accountEmail}</strong>
                {msStatus.active && <span className="optional-tag" style={{ marginLeft: 8 }}>{ta('in uso ora')}</span>}
              </p>
              <button className="btn btn-ghost" onClick={handleDisconnectMicrosoft} disabled={msDisconnecting}>
                {msDisconnecting ? ta('Scollegamento…') : ta('Scollega account')}
              </button>
            </>
          ) : (
            <button className="btn btn-primary" onClick={handleConnectMicrosoft}>{ta('Connetti con Microsoft')}</button>
          )}
        </div>
      )}

      <div className="admin-card" style={{ maxWidth: 480 }}>
        {msStatus?.active && (
          <p style={{ fontSize: '0.78rem', color: 'var(--muted-text)', marginBottom: 16 }}>
            {ta('L\'account Microsoft collegato qui sopra è attualmente in uso per l\'invio: questa configurazione SMTP resta salvata ma non viene usata finché l\'account Microsoft resta collegato.')}
          </p>
        )}
        {error && <div className="form-error">{error}</div>}
        {success && (
          <div style={{ background: 'rgba(62,142,126,0.12)', color: 'var(--teal-green-dark)', borderRadius: 'var(--radius-control)', padding: '10px 14px', fontSize: '0.85rem', marginBottom: 16 }}>
            {ta('Impostazioni salvate correttamente.')}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="field-row">
            <div className="field">
              <label>{ta('Host SMTP *')}</label>
              <input type="text" value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} placeholder={ta('smtp.esempio.it')} />
            </div>
            <div className="field">
              <label>{ta('Porta *')}</label>
              <input type="text" inputMode="numeric" value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })} />
            </div>
          </div>

          <label className="checkbox-row">
            <input type="checkbox" checked={form.secure} onChange={(e) => setForm({ ...form, secure: e.target.checked })} />
            <span>{ta('Connessione SSL/TLS diretta (tipicamente porta 465; lascia deselezionato per STARTTLS su 587)')}</span>
          </label>

          <div className="field">
            <label>{ta('Utente SMTP *')}</label>
            <input type="text" value={form.user} onChange={(e) => setForm({ ...form, user: e.target.value })} autoComplete="off" />
          </div>
          <div className="field">
            <label>{ta('Password SMTP')} {hasPassword && <span className="optional-tag">{ta('(lascia vuoto per non modificarla)')}</span>}</label>
            <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="new-password" />
          </div>

          <div className="field-row">
            <div className="field">
              <label>{ta('Nome mittente')}</label>
              <input type="text" value={form.fromName} onChange={(e) => setForm({ ...form, fromName: e.target.value })} />
            </div>
            <div className="field">
              <label>{ta('Email mittente *')}</label>
              <input type="text" value={form.fromEmail} onChange={(e) => setForm({ ...form, fromEmail: e.target.value })} />
            </div>
          </div>

          <button className="btn btn-primary" disabled={saving}>{saving ? ta('Salvataggio…') : ta('Salva impostazioni')}</button>
        </form>
      </div>

      <div className="admin-card" style={{ maxWidth: 480 }}>
        <h3 style={{ marginBottom: 14 }}>{ta('Invia una email di test')}</h3>
        <p style={{ fontSize: '0.85rem', color: 'var(--muted-text)', marginBottom: 14 }}>
          {ta('Verifica che la configurazione funzioni davvero, prima di affidarti a recupero password e notifiche.')}
        </p>
        {testStatus === 'ok' && (
          <div style={{ background: 'rgba(62,142,126,0.12)', color: 'var(--teal-green-dark)', borderRadius: 'var(--radius-control)', padding: '10px 14px', fontSize: '0.85rem', marginBottom: 16 }}>
            {ta('Email inviata: controlla la casella di posta.')}
          </div>
        )}
        {testStatus === 'error' && <div className="form-error">{testError}</div>}
        <form onSubmit={handleTest}>
          <div className="field-row" style={{ alignItems: 'end' }}>
            <div className="field" style={{ marginBottom: 0 }}>
              <label>{ta('Indirizzo destinatario')}</label>
              <input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder={ta('tua@email.it')} />
            </div>
            <button className="btn btn-ghost" disabled={testStatus === 'sending'} style={{ height: 44 }}>
              {testStatus === 'sending' ? ta('Invio…') : ta('Invia test')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
